import { memo, useMemo } from 'react';
import type { Capture } from '@draughts/shared';
import { useGameStore } from '../../store/game-store';
import { usePreferencesStore } from '../../store/preferences-store';
import { usePieceColorPair } from './use-appearance';
import Piece from './Piece';
import './CapturedPieces.css';

export interface CapturedPiecesProps {
  captures: Capture[];
  /** This player's captures minus the opponent's (material differential). */
  advantage?: number;
}

/** Max captured-piece icons shown before collapsing to an ellipsis. */
const MAX_ICONS = 5;
/** Rendered size (px) of each captured-piece SVG. */
const ICON_SIZE = 16;

export const CapturedPieces = memo<CapturedPiecesProps>(({ captures, advantage = 0 }) => {
  // Stessi selettori di GameBoard: senza `colorPair` il tema 'auto' (default)
  // ricadrebbe sul palette `classic`, mostrando icone di colore diverso dai pezzi
  // realmente sulla scacchiera in ogni variante non classica.
  const variant = useGameStore((state) => state.variant);
  const colorPair = usePieceColorPair(variant);

  // Show only the net material lead: when ahead, render that many icons taken
  // from this player's captures. The trailing/even player shows nothing.
  const surplus = advantage > 0 ? captures.slice(captures.length - advantage) : [];
  if (surplus.length === 0) return null;

  const shown = surplus.slice(0, MAX_ICONS);
  const hasMore = surplus.length > MAX_ICONS;

  return (
    <span
      className="captured-pieces"
      data-testid="captured-pieces"
      aria-label={`${surplus.length} pieces ahead`}
    >
      {shown.map((c, i) => (
        <span key={i} className="captured-pieces__piece">
          <svg
            className="captured-pieces__svg"
            width={ICON_SIZE}
            height={ICON_SIZE}
            viewBox={`0 0 ${ICON_SIZE} ${ICON_SIZE}`}
            aria-hidden="true"
          >
            {/* lod 'micro': a 16px un solco inciso e lo zigzag della corona sarebbero
                sub-pixel; il tier più basso li sostituisce con parete e disco pieno. */}
            <Piece piece={c.piece} x={ICON_SIZE / 2} y={ICON_SIZE / 2} size={ICON_SIZE} colorPair={colorPair} lod="micro" />
          </svg>
        </span>
      ))}
      {hasMore && (
        <span className="captured-pieces__more" data-testid="captured-pieces-more" aria-hidden="true">
          …
        </span>
      )}
    </span>
  );
});

CapturedPieces.displayName = 'CapturedPieces';
